import { SignupBody } from '@oresundsbron/api';
import { Typography, Well, Button, Box } from '@oresundsbron/bridge-ui';
import { Lens } from 'monocle-ts';
import { Trans, useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { ContractName } from '../../components/ContractName';
import { useLinks } from '../../hooks/useLinks';
import {
  getContractTranslationKey,
  TranslateableContracts,
} from '../../lib/contract-translations';
import { useSignUp } from './stores/useSignUp';
import { DefaultLink } from '~/components/Links/DefaultLink';

const contractType = Lens.fromPath<SignupBody>()(['contractType']);

const findBizzes = (data?: SignupBody) => {
  return (
    data?.payment.paymentEntries.filter(
      (paymentEntry) =>
        !!paymentEntry.licensePlate && paymentEntry.paymentOptions === 'bizz'
    ) ?? []
  );
};

const getLicensePlates = (data?: SignupBody) => {
  return (
    data?.payment.paymentEntries.filter(
      (paymentEntry) =>
        !!paymentEntry.licensePlate && paymentEntry.paymentOptions !== 'bizz'
    ) ?? []
  );
};

const hasBizz = (data?: SignupBody) => {
  return (
    data?.payment.paymentEntries.filter(
      (paymentEntry) => paymentEntry.paymentOptions === 'bizz'
    ).length ?? 0 > 0
  );
};

const getContract = (data?: SignupBody) => {
  if (!!data) {
    let contractString = contractType.get(data) as TranslateableContracts;
    let contractKey = getContractTranslationKey(contractString);
    if (contractKey._tag === 'Some') {
      return <ContractName contract={contractKey.value} />;
    } else {
      return '';
    }
  } else {
    return '';
  }
};

export const ThankYou: FC<{ ns: string }> = ({ ns }) => {
  const { t } = useTranslation([ns, 'common'], { nsMode: 'fallback' });
  const { data } = useSignUp();
  const { findLinkByCodeRef } = useLinks();

  const bizzLicensePlates = useMemo(() => findBizzes(data), [data]);
  const licensePlates = useMemo(() => getLicensePlates(data), [data]);
  const contract = useMemo(() => getContract(data), [data]);
  const bizzPresent = useMemo(() => hasBizz(data), [data]);
  const isInvoice = useMemo(
    () => data?.paymentMethod?.method === 'invoice',
    [data]
  );

  const [licensePlate, secondLicensePlate] = licensePlates;
  const [licensePlateBizz, secondLicensePlateBizz] = bizzLicensePlates;

  const secLink = useMemo(() => {
    let link = findLinkByCodeRef('security-on-bridge');
    if (link._tag === 'Some') {
      return link.value.path;
    } else {
      return undefined;
    }
  }, [findLinkByCodeRef]);

  const clubLink = useMemo(() => {
    let link = findLinkByCodeRef('club');
    if (link._tag === 'Some') {
      return link.value.path;
    } else {
      return undefined;
    }
  }, [findLinkByCodeRef]);

  return (
    <>
      <Typography as="h1" intent="headline" className="col-span-full">
        {!data ? (
          t('form.sections.confirmation.shortTitle')
        ) : (
          <Trans ns={ns} i18nKey="form.sections.confirmation.title">
            {contract}
          </Trans>
        )}
      </Typography>
      {data && (
        <Typography className="col-span-full md:col-span-8">
          <Trans ns={ns} i18nKey="form.sections.confirmation.description">
            {contract}
            <DefaultLink href={clubLink || ''} />
          </Trans>
        </Typography>
      )}
      <Well className="col-span-full md:col-span-8" transparent={false}>
        {data && (
          <Typography intent="title" className="mb-2">
            {t('form.sections.confirmation.readyToDrive.title')}
          </Typography>
        )}
        {isInvoice ? (
          <Typography className={bizzPresent ? 'mb-2' : 'mb-4'}>
            {t('form.sections.confirmation.readyToDrive.invoice')}
          </Typography>
        ) : data ? (
          <Typography className={bizzPresent ? 'mb-2' : 'mb-4'}>
            <Trans
              ns={ns}
              i18nKey="form.sections.confirmation.readyToDrive.description"
              values={{
                count: licensePlates.length,
                licensePlate: licensePlate?.licensePlate,
                secondLicensePlate: secondLicensePlate?.licensePlate,
              }}
            >
              <Typography as="span" weight="bold" className="uppercase" />
            </Trans>
          </Typography>
        ) : null}
        {bizzLicensePlates.length ? (
          <Typography className="mb-4">
            <Trans
              ns={ns}
              i18nKey="form.sections.confirmation.readyToDrive.registerBizz"
              values={{
                count: bizzLicensePlates.length,
                licensePlate: licensePlateBizz?.licensePlate,
                secondLicensePlate: secondLicensePlateBizz?.licensePlate,
              }}
            >
              <Typography as="span" weight="bold" className="mb-4 uppercase" />
            </Trans>
          </Typography>
        ) : bizzPresent ? (
          <Typography className="mb-4">
            {t('form.sections.confirmation.readyToDrive.bizz')}
          </Typography>
        ) : null}
        <Typography intent="title" className="mb-2">
          {t('form.sections.confirmation.myAccount.title')}
        </Typography>
        <Typography className="mb-4">
          <Trans
            ns={ns}
            i18nKey="form.sections.confirmation.myAccount.description"
          >
            <DefaultLink href="/account/login" />
          </Trans>
        </Typography>
        <Typography intent="title" className="mb-2">
          {t('form.sections.confirmation.security.title')}
        </Typography>
        <Typography className="mb-6">
          <Trans
            ns={ns}
            i18nKey="form.sections.confirmation.security.description"
          >
            <DefaultLink href={secLink || ''} />
          </Trans>
        </Typography>
        <Box className="flex justify-end">
          <Button as={DefaultLink} href="/account/login" underline={false}>
            {t('action.myAccount')}
          </Button>
        </Box>
      </Well>
    </>
  );
};
